import { ChevronDown } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';
import { needsCountryChoice } from '../../../shared/country/countryConfig';
import { useCountryStore } from '../../../shared/country/countryStore';
import { useI18nStore } from '../../../shared/i18n/i18nStore';
import { SheetPortal } from '../../../shared/ui/SheetPortal';
import { CountryFlag } from '../../../shared/country/CountryFlag';

/** Anchored list geometry, measured from the trigger at open time. */
type Anchor = { left: number; width: number; top?: number; bottom?: number; maxHeight: number };

const LIST_MIN_WIDTH = 216;
const VIEWPORT_MARGIN = 12;
/** Below this much room under the trigger, the list opens upward instead. */
const FLIP_THRESHOLD = 150;

/**
 * One control for country and calling code, living *inside* the phone field.
 *
 * There used to be two — a labelled "country / region" field and a read-only prefix
 * chip — both writing the same store value. The read-only half existed precisely
 * because two controls setting one value invite disagreement; merging them removes
 * the reason for that workaround.
 *
 * It also moves where a wrong country is visible: it now sits beside the field the
 * player is typing into, instead of in a row above that they scrolled past.
 *
 * Not a native <select>: <option> cannot hold an SVG, and the flags are the point.
 * Everything a native select gave us for free is re-implemented below — listbox
 * roles, arrow keys, Home/End, Escape, Tab-out, focus return.
 */
export function CountryPicker({ showPhonePrefix = true }: { showPhonePrefix?: boolean } = {}) {
  const t = useI18nStore((state) => state.t);
  const countries = useCountryStore((state) => state.countries);
  const country = useCountryStore((state) => state.country);
  const selectCountry = useCountryStore((state) => state.selectCountry);

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [active, setActive] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  // Layout, not passive: this runs after the DOM commits but before the browser
  // paints, so the freshly-opened list never shows a stale `aria-activedescendant`
  // from whatever was highlighted last time it was open (a passive effect would
  // leave that wrong value on screen, and to a screen reader, for one frame). The
  // list's own focus() lives here too, for the same reason, and because it makes
  // the ref below a plain DOM ref instead of an inline callback that would re-run
  // — and re-focus — on every keystroke-driven render.
  useLayoutEffect(() => {
    if (!open) return;
    setActive(Math.max(0, countries.findIndex((row) => row.code === country.code)));
    listRef.current?.focus();
  }, [open, countries, country.code]);

  /**
   * Where the list sits, from the trigger's current rect.
   *
   * <p>The list is portaled (an ancestor with `overflow` would otherwise clip it),
   * so it cannot inherit the trigger's position from the DOM — it has to be
   * measured.
   */
  function measure(): Anchor | null {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const width = Math.max(LIST_MIN_WIDTH, rect.width);
    const below = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const above = rect.top - VIEWPORT_MARGIN;
    // Keep the whole list on screen horizontally even when the trigger sits near
    // the right edge (it does not today, but a RTL market would put it there).
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    return below < FLIP_THRESHOLD && above > below
      ? { left, width, bottom: window.innerHeight - rect.top + 6, maxHeight: above - 6 }
      : { left, width, top: rect.bottom + 6, maxHeight: below - 6 };
  }

  function openList() {
    const next = measure();
    if (!next) return;
    setAnchor(next);
    setOpen(true);
  }

  /**
   * Keeps the list on the trigger while it is open.
   *
   * <p>Measuring once at open time is not enough, for two reasons that both bite
   * on a phone. The auth forms scroll the focused field into view
   * (`scrollAuthInputIntoView` on the form's `onFocusCapture`), and focusing this
   * trigger fires it — so the page moves *after* the click handler measured, and
   * the list was left 16px adrift of the field. And a `position: fixed` list does
   * not follow the page at all, so any later scroll, or the on-screen keyboard
   * resizing the viewport, would detach it completely.
   *
   * <p>Capture phase: the scroll happens on whichever ancestor actually scrolls,
   * and scroll events do not bubble to `window`.
   */
  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = () => setAnchor((current) => measure() ?? current);
    reposition();
    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      window.visualViewport?.removeEventListener('resize', reposition);
    };
    // `measure` reads only refs and the live viewport, so it needs no deps of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function commit(index: number) {
    const row = countries[index];
    if (row) selectCountry(row.code);
    close();
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((i) => Math.min(i + 1, countries.length - 1)); return; }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
    if (event.key === 'Home') { event.preventDefault(); setActive(0); return; }
    if (event.key === 'End') { event.preventDefault(); setActive(countries.length - 1); return; }
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); commit(active); }
  }

  /**
   * Tab does not carry a key we can intercept the way Escape's `preventDefault`
   * does — the browser has already decided where focus goes next by the time this
   * fires. So this only closes; it never calls `close()` (which re-focuses the
   * trigger). Stealing focus back would fight Tab's own destination and turn a
   * plain "move to the next field" into a trap that snaps back to this button —
   * same failure shape as never closing at all, just louder.
   *
   * Guarded by `relatedTarget`: clicking an option also blurs the list (options
   * are plain `<li>`s, not focusable), but `relatedTarget` there is whatever the
   * click lands on next, not something inside the list — the `onMouseDown`
   * `preventDefault` below stops that blur from firing in the first place, so
   * `commit` (not this) is what closes a click-driven selection.
   */
  function onListBlur(event: FocusEvent<HTMLUListElement>) {
    const next = event.relatedTarget as Node | null;
    if (next && listRef.current?.contains(next)) return;
    setOpen(false);
  }

  // One active market means nothing to choose. The segment still renders — the
  // player needs to see the prefix their number is being built with — but it is
  // inert.
  const choosable = needsCountryChoice(countries);
  if (!countries.length) return <span className="text-ink-3">—</span>;

  return (
    <div className="auth-cc" data-phone-prefix={showPhonePrefix ? country.code : undefined} data-country-select="true">
      <button
        ref={triggerRef}
        type="button"
        className="auth-cc-trigger tap"
        aria-label={t('registerCountryPickerLabel')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={!choosable}
        onClick={() => (open ? close() : openList())}
      >
        <CountryFlag code={country.code} size={17} />
        <span className="auth-cc-dial">{showPhonePrefix ? `+${country.phonePrefix}` : country.code}</span>
        {choosable ? <ChevronDown size={12} aria-hidden="true" className="auth-cc-caret" /> : null}
      </button>

      {open && anchor ? (
        <SheetPortal>
          <div
            className="auth-cc-scrim"
            onClick={close}
            // Same reason as the list's own onMouseDown below: the backdrop isn't
            // focusable, so without this its mousedown blurs the list to <body>
            // first, onListBlur closes it (by design, without refocusing), and by
            // the time the click fires there's no open picker left for `close()`
            // to act on — a tap on the backdrop would drop focus on the floor
            // instead of returning it to the trigger. Preventing the mousedown's
            // default keeps focus on the list until the click's own close() runs.
            onMouseDown={(event) => event.preventDefault()}
          />
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className="auth-cc-list"
            style={{
              left: anchor.left,
              width: anchor.width,
              top: anchor.top,
              bottom: anchor.bottom,
              maxHeight: anchor.maxHeight,
            }}
            tabIndex={-1}
            aria-activedescendant={`${listId}-${active}`}
            onKeyDown={onKeyDown}
            onBlur={onListBlur}
            // Keeps focus (and thus the list) alive through a mouse click on an
            // option — without this, the click's own mousedown blurs the list
            // first (options aren't focusable, so focus has nowhere else to go),
            // `onListBlur` closes it, and the option that was clicked is gone
            // from the DOM before its `click` ever fires.
            onMouseDown={(event) => event.preventDefault()}
          >
            {countries.map((row, index) => (
              <li
                key={row.code}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={row.code === country.code}
                className={`auth-cc-option${index === active ? ' is-active' : ''}`}
                onClick={() => commit(index)}
                onMouseEnter={() => setActive(index)}
              >
                <CountryFlag code={row.code} size={20} />
                {/* Calling code first, at a fixed width: it is what the trigger
                    shows and therefore what the player is scanning for. The
                    currency is deliberately not repeated here — the registration
                    notice states it on its own line, where a wrong country is
                    actually caught. */}
                {showPhonePrefix && <span className="auth-cc-option-dial">+{row.phonePrefix}</span>}
                <span className="auth-cc-option-name">{row.name}</span>
              </li>
            ))}
          </ul>
        </SheetPortal>
      ) : null}
    </div>
  );
}

/**
 * "We detected your country" — a hint line, so it belongs under the field with
 * the other hints rather than inside it.
 *
 * <p>It used to render inside the picker's wrapper, which worked only while that
 * wrapper was a block sitting beside the input. The picker is now one segment of
 * a horizontal field, and a paragraph cannot live in that row.
 *
 * <p>Shows only while the highlight is still the machine's guess. Once the player
 * picks, the line would be claiming credit for their choice.
 */
export function CountryDetectedHint() {
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);
  const suggestedCode = useCountryStore((state) => state.suggestedCode);
  const selectedCode = useCountryStore((state) => state.selectedCode);
  if (selectedCode || suggestedCode !== country.code) return null;
  return <p className="auth-cc-detected">{t('registerCountryDetected')}</p>;
}
